import { type CalcResult, err, isPositiveFinite, ok } from "../types.ts";
import {
  type OffsetAngle,
  OFFSET_MULTIPLIER,
  OFFSET_SHRINK_PER_INCH,
  SADDLE_CENTER_TO_OUTER_MULTIPLIER,
  SADDLE_SHRINK_PER_INCH,
  STUB_TAKE_UP_IN,
} from "../tables/bending.ts";

export type OffsetBendInput = {
  kind: "offset";
  angleDeg: OffsetAngle; // 10 | 22.5 | 30 | 45 | 60
  offsetDepthIn: number; // how far the run must shift
};

export type OffsetBendResult = {
  kind: "offset";
  distanceBetweenBendsIn: number; // mark-to-mark spacing
  shrinkIn: number; // conduit consumed by the offset
};

export type Stub90Input = {
  kind: "stub90";
  tradeSize: string; // must have a take-up figure (1/2, 3/4, 1, 1-1/4)
  stubHeightIn: number; // desired finished stub height
};

export type Stub90Result = {
  kind: "stub90";
  takeUpIn: number;
  bendMarkIn: number; // distance from end of conduit to the bend mark
};

export type Saddle3Input = {
  kind: "saddle3";
  obstructionHeightIn: number; // height of the obstacle to clear
};

export type Saddle3Result = {
  kind: "saddle3";
  centerToOuterMarksIn: number; // distance from center mark to each outer mark
  shrinkIn: number;
  centerAngleDeg: 45;
  outerAngleDeg: 22.5;
};

export type ConduitBendingInput = OffsetBendInput | Stub90Input | Saddle3Input;
export type ConduitBendingResult = OffsetBendResult | Stub90Result | Saddle3Result;

/**
 * Conduit bending math (Ugly's standard figures).
 *  - offset:  distance between marks = multiplier × offset depth;
 *             shrink = shrink-per-inch × offset depth.
 *  - stub90:  bend mark = stub height − take-up (by trade size).
 *  - saddle3: center-to-outer marks = 2.5 × obstruction height;
 *             shrink = 3/16" × obstruction height. (45° center, 22.5° outers.)
 */
export function conduitBending(input: ConduitBendingInput): CalcResult<ConduitBendingResult> {
  switch (input.kind) {
    case "offset": {
      const { angleDeg, offsetDepthIn } = input;
      if (!(angleDeg in OFFSET_MULTIPLIER)) {
        return err(`Offset angle must be one of 10, 22.5, 30, 45, 60°.`);
      }
      if (!isPositiveFinite(offsetDepthIn)) {
        return err("Offset depth (in) must be a positive number.");
      }
      return ok({
        kind: "offset",
        distanceBetweenBendsIn: OFFSET_MULTIPLIER[angleDeg] * offsetDepthIn,
        shrinkIn: OFFSET_SHRINK_PER_INCH[angleDeg] * offsetDepthIn,
      });
    }
    case "stub90": {
      const { tradeSize, stubHeightIn } = input;
      const takeUp = STUB_TAKE_UP_IN[tradeSize];
      if (takeUp === undefined) {
        return err(`No take-up figure for trade size "${String(tradeSize)}".`);
      }
      if (!isPositiveFinite(stubHeightIn)) {
        return err("Stub height (in) must be a positive number.");
      }
      if (stubHeightIn <= takeUp) {
        return err(
          `Stub height (${stubHeightIn}") must be greater than the take-up (${takeUp}") for this size.`,
        );
      }
      return ok({ kind: "stub90", takeUpIn: takeUp, bendMarkIn: stubHeightIn - takeUp });
    }
    case "saddle3": {
      const { obstructionHeightIn } = input;
      if (!isPositiveFinite(obstructionHeightIn)) {
        return err("Obstruction height (in) must be a positive number.");
      }
      return ok({
        kind: "saddle3",
        centerToOuterMarksIn: SADDLE_CENTER_TO_OUTER_MULTIPLIER * obstructionHeightIn,
        shrinkIn: SADDLE_SHRINK_PER_INCH * obstructionHeightIn,
        centerAngleDeg: 45,
        outerAngleDeg: 22.5,
      });
    }
    default: {
      // Exhaustiveness guard — unknown kind never returns a number.
      return err("Unknown bend type.");
    }
  }
}
